import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../../hooks/useEndpoints';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import styles from './BodyMapWeeklyWidget.module.scss';

interface BodyMapWeeklyWidgetProps {
  athleteId: string;
}

interface IBodyMapWeeklySummary {
  zoneCount: number;
  persistentCount: number;
  domsCappedCount: number;
}

export const BodyMapWeeklyWidget: FC<BodyMapWeeklyWidgetProps> = ({ athleteId }) => {
  const endpoints = useEndpoints();

  const { data, isLoading, isError, refetch } = useQuery<IBodyMapWeeklySummary>({
    queryKey: ['body-map-weekly-summary', athleteId],
    queryFn: () => endpoints.getBodyMapWeeklySummary(athleteId).then((r) => r.data),
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div className={styles.stateWrap}>
        <LeopartLoader size="sm" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.stateWrap}>
        <ChartErrorState onRetry={() => refetch()} />
      </div>
    );
  }

  // Nessun dato o settimana senza segnalazioni: banner informativo invece del
  // return null silenzioso (l'assenza di fastidi è un'informazione utile).
  if (!data || data.zoneCount === 0) {
    return (
      <div className={styles.container}>
        <span className={styles.emptyText}>Nessun fastidio segnalato nell'ultima settimana</span>
      </div>
    );
  }

  const items = [
    { label: 'zone segnalate', value: data.zoneCount, className: styles.valueNeutral },
    { label: 'persistenti', value: data.persistentCount, className: styles.valueWarning },
    { label: 'cappati DOMS', value: data.domsCappedCount, className: styles.valueMuted },
  ];

  return (
    <div className={styles.container}>
      <span className={styles.title}>Fastidi ultima settimana:</span>
      {items.map(({ label, value, className }, idx) => (
        <span key={label} className={styles.item}>
          <span className={className}>{value}</span>
          {' '}{label}
          {idx < items.length - 1 && <span className={styles.separator}>·</span>}
        </span>
      ))}
    </div>
  );
};

export default BodyMapWeeklyWidget;
